/**
 * Utility functions for Problem Plan formatting and integration
 */

// Plan categories configuration
export const PLAN_CATEGORIES = [
  {
    key: 'general',
    label: 'General',
    label_vn: 'Tổng quát',
    placeholder: 'Enter general plan details...',
    placeholder_vn: 'Nhập chi tiết kế hoạch tổng quát...'
  },
  {
    key: 'medications',
    label: 'Medications',
    label_vn: 'Thuốc',
    placeholder: 'Enter medication plan...',
    placeholder_vn: 'Nhập kế hoạch thuốc...'
  },
  {
    key: 'labs',
    label: 'Labs',
    label_vn: 'Xét nghiệm',
    placeholder: 'Enter laboratory plan...',
    placeholder_vn: 'Nhập kế hoạch xét nghiệm...'
  },
  {
    key: 'diagnostics',
    label: 'Diagnostics',
    label_vn: 'Chẩn đoán',
    placeholder: 'Enter diagnostic plan...',
    placeholder_vn: 'Nhập kế hoạch chẩn đoán...'
  },
  {
    key: 'referrals',
    label: 'Referrals',
    label_vn: 'Giới thiệu',
    placeholder: 'Enter referral plan...',
    placeholder_vn: 'Nhập kế hoạch giới thiệu...'
  },
  {
    key: 'lifestyle',
    label: 'Lifestyle',
    label_vn: 'Lối sống',
    placeholder: 'Enter lifestyle plan...',
    placeholder_vn: 'Nhập kế hoạch lối sống...'
  }
];

/**
 * Check if a problem has any plan content
 * @param {Object} problem - Problem object with plan field
 * @returns {boolean} - True if problem has any plan content
 */
export const hasPlanContent = (problem) => {
  if (!problem?.plan) return false;
  
  return PLAN_CATEGORIES.some(category => 
    problem.plan[category.key] && problem.plan[category.key].trim()
  );
};

/**
 * Get non-empty plan categories for a problem
 * @param {Object} problem - Problem object with plan field
 * @returns {Array} - Array of plan categories with content
 */
export const getNonEmptyPlanCategories = (problem) => {
  if (!problem?.plan) return [];
  
  return PLAN_CATEGORIES.filter(category => 
    problem.plan[category.key] && problem.plan[category.key].trim()
  ).map(category => ({
    ...category,
    content: problem.plan[category.key].trim()
  }));
};

/**
 * Format problem plan as HTML for Assessment & Plan integration
 * @param {Object} problem - Problem object with plan field
 * @param {string} language - Language code ('en' or 'vi')
 * @returns {string} - Formatted HTML string
 */
export const formatProblemPlanAsHtml = (problem, language = 'en') => {
  const nonEmptyCategories = getNonEmptyPlanCategories(problem);
  
  if (nonEmptyCategories.length === 0) return '';
  
  let planHtml = '<ul>';
  nonEmptyCategories.forEach(category => {
    const label = language === 'vi' ? category.label_vn : category.label;
    planHtml += `<li><strong>${label}:</strong> ${category.content}</li>`;
  });
  planHtml += '</ul>';
  
  return planHtml;
};

/**
 * Build Assessment & Plan text from selected problems with plan integration
 * @param {Array} selectedProblems - Array of selected problem objects
 * @param {string} language - Language code ('en' or 'vi')
 * @returns {string} - Formatted HTML string for Assessment & Plan
 */
export const buildAssessmentAndPlanText = (selectedProblems, language = 'en') => {
  if (!Array.isArray(selectedProblems) || selectedProblems.length === 0) {
    return '';
  }

  const assessmentLabel = language === 'vi' ? 'Đánh giá & Kế hoạch:' : 'Assessment & Plan:';
  let assessmentHtml = `<p><strong>${assessmentLabel}</strong></p><ul>`;
  
  selectedProblems.forEach(problem => {
    assessmentHtml += `<li><strong>${problem.problemName}</strong>`;
    
    if (problem.icdCode) {
      assessmentHtml += ` (${problem.icdCode})`;
    }
    
    if (problem.notes) {
      assessmentHtml += ` - ${problem.notes}`;
    }
    
    // Add plan details if they exist
    const planHtml = formatProblemPlanAsHtml(problem, language);
    if (planHtml) {
      assessmentHtml += planHtml;
    }
    
    assessmentHtml += '</li>';
  });
  
  assessmentHtml += '</ul>';
  return assessmentHtml;
};

/**
 * Build MD Recommendations text from selected problems with plan integration
 * @param {Array} selectedProblems - Array of selected problem objects
 * @param {string} language - Language code ('en' or 'vi')
 * @returns {string} - Formatted HTML string for MD Recommendations
 */
export const buildMdRecommendationsText = (selectedProblems, language = 'en') => {
  if (!Array.isArray(selectedProblems) || selectedProblems.length === 0) {
    return '';
  }

  const recommendationsLabel = language === 'vi' ? 'Khuyến nghị của Bác sĩ:' : 'MD Recommendations:';
  let recommendationsHtml = `<p><strong>${recommendationsLabel}</strong></p><ul>`;
  
  selectedProblems.forEach(problem => {
    recommendationsHtml += `<li><strong>${problem.problemName}</strong>`;
    
    if (problem.icdCode) {
      recommendationsHtml += ` (${problem.icdCode})`;
    }
    
    // For MD recommendations, focus on plan content
    const planHtml = formatProblemPlanAsHtml(problem, language);
    if (planHtml) {
      recommendationsHtml += planHtml;
    } else if (problem.notes) {
      // Fallback to notes if no plan is available
      recommendationsHtml += ` - ${problem.notes}`;
    }
    
    recommendationsHtml += '</li>';
  });
  
  recommendationsHtml += '</ul>';
  return recommendationsHtml;
};

/**
 * Format plan content as plain text (for display purposes)
 * @param {Object} problem - Problem object with plan field
 * @param {string} language - Language code ('en' or 'vi')
 * @returns {string} - Formatted plain text string
 */
export const formatProblemPlanAsText = (problem, language = 'en') => {
  const nonEmptyCategories = getNonEmptyPlanCategories(problem);
  
  if (nonEmptyCategories.length === 0) return '';
  
  let planText = '';
  nonEmptyCategories.forEach((category, index) => {
    const label = language === 'vi' ? category.label_vn : category.label;
    if (index > 0) planText += '\n';
    planText += `${label}: ${category.content}`;
  });
  
  return planText;
};

/**
 * Validate plan data
 * @param {Object} planData - Plan data object
 * @returns {Object} - Validation result with isValid and errors
 */
export const validatePlanData = (planData) => {
  const errors = [];
  const maxLength = 2000;
  
  if (!planData || typeof planData !== 'object') {
    return { isValid: false, errors: ['Plan data must be an object'] };
  }
  
  PLAN_CATEGORIES.forEach(category => {
    const value = planData[category.key];
    if (value && typeof value === 'string' && value.length > maxLength) {
      errors.push(`${category.label} plan cannot exceed ${maxLength} characters`);
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors
  };
};
